// src/components/gallery/CardViewerModal.tsx
// Modal to display a single 3D card on the left
import React, { useState, useEffect, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import XorPixViewer from '../xorpix/XorPixViewer';

// Character item interface
interface CharacterItem {
  id: string;
  name: string;
  nameReading?: string;
  nameEnglish?: string;
  category?: 'original' | 'vtuber';
  bio?: string;
  // XOR-Pix fields (CDN primary)
  noise1Url?: string;
  noise2Url?: string;
  rid?: string;
  imageWidth?: number;
  imageHeight?: number;
  thumbnailUrl?: string;
  // XOR-Pix fallback URLs (signed GCS URLs)
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
  // Legacy fields
  avatarUrl?: string;
  headerImageUrl?: string;
  tags?: string[];
  cardTheme?: 'dark' | 'light';
  ownerId: string;
  ownerName?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
}

interface CardViewerModalProps {
  character: CharacterItem | null;
  isOpen: boolean;
  onClose: () => void;
}

// 高解像度カード: DOM要素は2倍サイズで描画し、
// Three.js group scale=0.5 で3D空間上のサイズを元と同じに保つ
// → ブラウザが576×864でラスタライズするため画質が向上
const cardWidth = 576;
const cardHeight = 864;
const totalWidth = cardWidth + 32;  // p-4 = 16px × 2
const totalHeight = cardHeight + 32;

// Single 3D Card
const Card3D: React.FC<{ character: CharacterItem }> = ({ character }) => {
  const { t } = useTranslation('top');
  const [imageError, setImageError] = useState(false);
  const [xorLoaded, setXorLoaded] = useState(false);

  const cardTheme = character.cardTheme || 'dark';
  const hasXorPix = character.noise1Url && character.noise2Url && character.rid;
  const fallbackImageUrl = character.thumbnailUrl || character.avatarUrl || character.headerImageUrl;
  const displayName = character.ownerName || character.ownerUsername || 'Creator';
  const username = character.ownerUsername || '';

  return (
    // group scale=0.5 で3D空間上の見た目サイズを半分に
    // → distanceFactor=1.2 + 元のカメラ設定で元と同じ見た目サイズになる
    <group scale={[0.5, 0.5, 0.5]}>
      {/* Card edge - カードの厚み */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[0.6, 0.9, 0.01]} />
        <meshStandardMaterial color="#e0e0e0" metalness={0.5} roughness={0.3} />
      </mesh>

      {/* Front */}
      <Html
        position={[0, 0, 0.006]}
        transform
        distanceFactor={1.2}
        center
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="bg-white p-4 rounded-[2rem] shadow-2xl select-none"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          <div
            className="relative rounded-[1.5rem] overflow-hidden"
            style={{ width: `${cardWidth}px`, height: `${cardHeight}px` }}
          >
            {hasXorPix && !imageError ? (
              <XorPixViewer
                noise1Url={character.noise1Url!}
                noise2Url={character.noise2Url!}
                rid={character.rid!}
                width={character.imageWidth || 800}
                height={character.imageHeight || 1200}
                characterId={character.id}
                fallbackNoise1Url={character.fallbackNoise1Url}
                fallbackNoise2Url={character.fallbackNoise2Url}
                fitMode="cover"
                className="absolute inset-0 w-full h-full"
                onLoad={() => setXorLoaded(true)}
                onError={() => setImageError(true)}
              />
            ) : fallbackImageUrl && !imageError ? (
              <img
                src={fallbackImageUrl}
                alt={character.name}
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setImageError(true)}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
                <PhotoIcon className="w-12 h-12 text-gray-300" />
                <span className="text-gray-400 text-sm mt-2">{t('cardViewer.noImage')}</span>
              </div>
            )}

            <div className={`absolute top-0 left-0 right-0 pt-5 pb-16 px-8 bg-gradient-to-b ${
              cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
            }`}>
              <span className={`text-[20px] font-medium tracking-[0.2em] uppercase ${
                cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
              }`}>
                ORIFOLI
              </span>
            </div>

            {/* English Name - Top Right, Vertical (bottom to top) */}
            {character.nameEnglish && (
              <div
                className="absolute top-8 right-2 z-10"
                style={{
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                }}
              >
                <span className={`text-[20px] font-light tracking-wider whitespace-nowrap ${
                  cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
                }`}>
                  {character.nameEnglish}
                </span>
              </div>
            )}

            <div className={`absolute bottom-0 left-0 right-0 pt-40 pb-6 px-8 bg-gradient-to-t ${
              cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
            }`}>
              <p className={`text-[2.5rem] font-semibold tracking-tight line-clamp-2 ${
                cardTheme === 'dark' ? 'text-white' : 'text-black/60'
              }`} style={{ lineHeight: '1.2' }}>
                {character.name}
              </p>

              {/* Name Reading (よみがな) */}
              {character.nameReading && (
                <p className={`text-[18px] mt-1 ${
                  cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
                }`}>
                  {character.nameReading}
                </p>
              )}

              {character.bio && (
                <p className={`text-[20px] mt-2 line-clamp-2 leading-relaxed ${
                  cardTheme === 'dark' ? 'text-white/70' : 'text-gray-600'
                }`}>
                  {character.bio}
                </p>
              )}

              {character.tags && character.tags.length > 0 && (
                <div className="flex gap-2 mt-1 min-h-[28px]">
                  {character.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      className={`text-[18px] ${cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'}`}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <div className={`border-t my-6 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full overflow-hidden flex-shrink-0 relative ${
                  cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${
                    cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
                  }`} />
                  {xorLoaded && character.ownerAvatar && (
                    <img
                      src={character.ownerAvatar}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity"
                      onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                </div>
                <p className={`text-[18px] tracking-wide truncate ${
                  cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
                }`}>
                  <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>
                  {displayName}
                  {username && <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}> @{username}</span>}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Html>

      {/* Back */}
      <Html
        position={[0, 0, -0.006]}
        rotation={[0, Math.PI, 0]}
        transform
        distanceFactor={1.2}
        center
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="rounded-[2rem] shadow-2xl flex items-center justify-center select-none"
          style={{
            width: `${totalWidth}px`,
            height: `${totalHeight}px`,
            backgroundColor: cardTheme === 'dark' ? '#2a2a2a' : '#f0f0f0',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        >
          <span className={`text-5xl font-semibold tracking-[0.3em] ${
            cardTheme === 'dark' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            ORIFOLI
          </span>
        </div>
      </Html>
    </group>
  );
};

// Scene
const CardScene: React.FC<{ character: CharacterItem }> = ({ character }) => {
  return (
    <>
      <ambientLight intensity={0.8} />
      <pointLight position={[10, 10, 10]} intensity={0.5} />

      <Card3D character={character} />

      <OrbitControls
        enableZoom={true}
        enablePan={false}
        enableRotate={true}
        minDistance={1.5}
        maxDistance={15}
        autoRotate={true}
        autoRotateSpeed={1.5}
      />
    </>
  );
};

// Main Modal Component
const CardViewerModal: React.FC<CardViewerModalProps> = ({ character, isOpen, onClose }) => {
  const { t } = useTranslation('top');
  const navigate = useNavigate();
  const [cameraZ, setCameraZ] = useState(2.5);

  // 画面高さに応じてカメラ距離とY位置、ボタン位置を計算
  const [cameraY, setCameraY] = useState(0);
  const [buttonBottom, setButtonBottom] = useState(80);

  useEffect(() => {
    const updateLayout = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      // カードが画面高さの70%程度を占めるように調整
      // 小さい画面(600px)では遠く、大きい画面(1200px)では近く
      const distance = 1.5 + (600 / vh) * 1.5;
      setCameraZ(Math.min(Math.max(distance, 1.8), 3.5));

      // スマホ（縦画面）ではボトムバーを考慮してカメラを少し上げる
      if (vw < 768) {
        setCameraY(-0.3);
        setButtonBottom(Math.max(vh * 0.18, 80));
      } else {
        setCameraY(0);
        setButtonBottom(Math.max(vh * 0.10, 80));
      }
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  if (!isOpen || !character) return null;

  const handleViewDetails = () => {
    onClose();
    navigate(`/character/${character.id}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-[2] p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      >
        <XMarkIcon className="w-6 h-6 text-white" />
      </button>

      {/* 3D Canvas */}
      <div className="absolute inset-0 z-[1] flex items-center justify-center">
        <Canvas
          camera={{ position: [0, cameraY, cameraZ], fov: 50 }}
          dpr={[1, 2]}
          gl={{ antialias: true }}
          style={{ width: '100%', height: '100%', background: 'transparent' }}
        >
          <Suspense fallback={null}>
            <CardScene character={character} />
          </Suspense>
        </Canvas>
      </div>

      {/* Bottom actions */}
      <div
        className="absolute left-0 right-0 flex flex-col items-center gap-4 z-[2]"
        style={{ bottom: `${buttonBottom}px` }}
      >
        <button
          onClick={handleViewDetails}
          className="px-6 py-2 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          {t('cardViewer.viewDetails')}
        </button>
        <div className="text-white/40 text-xs">
          {t('cardViewer.hint')}
        </div>
      </div>
    </div>
  );
};

export default CardViewerModal;
